import type { Currency } from '@ps/web-core-enums';
import type { TrackedTranslation } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import classNames from 'clsx';
import React from 'react';
import { Field } from 'react-final-form';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import flags from '@swf/core/assets/flags';
import useValidator from '@swf/core/hooks/useValidator';

import type { VoucherAmount } from '../../../../../../types/Cashier';
import { isZeroExponentCurrency } from '../../../../../../utils/cashier';
import Button from '../../../../../Button/Button';

import {
  avatar,
  button,
  buttons,
  container,
  endAdornment,
  selection,
  title,
  voucherLabel,
  voucherSuggestionSelection,
  vouchersContainer
} from './SuggestionsSelection.scss';

interface Props {
  fieldName: string;
  suggestions: (number | VoucherAmount)[];
  currency: Currency;
  label: TrackedTranslation;
  isVoucher?: boolean;
}

interface SuggestionType {
  amount: number;
}

const SuggestionsSelection: React.FC<Props> = ({
  suggestions,
  currency,
  fieldName,
  label,
  isVoucher
}) => {
  const { buttonPressed } = useTrackers();
  const trackingLabel = useTrackingLabel(label);
  const [validator] = useValidator(required());

  const mapSuggestions = (suggestion: number | VoucherAmount): SuggestionType => {
    if (isVoucher) {
      const { amount } = suggestion as VoucherAmount;
      return { amount };
    } else {
      return { amount: suggestion as number };
    }
  };

  return (
    <Field name={fieldName} validate={isVoucher ? validator : undefined}>
      {({ input: { onChange, value, onBlur } }) => (
        <div className={isVoucher ? vouchersContainer : container}>
          <div className={title}>
            <Translation {...label} />
            {isVoucher && (
              <label className={endAdornment}>
                <img src={flags[currency.substring(0, 2)]} className={avatar} alt={currency} />
                {currency}
              </label>
            )}
          </div>

          <div className={buttons}>
            {suggestions.map((item) => {
              const { amount } = mapSuggestions(item);

              const dollars = amount / 100;
              const selected = +value === dollars;
              const fractionDigits = dollars % 1 === 0 ? 0 : 2;

              return (
                <Button
                  key={amount}
                  variant="secondary"
                  size="small"
                  className={classNames(button, {
                    [selection]: selected && !isVoucher,
                    [voucherSuggestionSelection]: selected && isVoucher,
                    [voucherLabel]: isVoucher
                  })}
                  selected={selected}
                  onClick={() => {
                    onChange(dollars.toString());
                    buttonPressed({ buttonName: `${trackingLabel} ${dollars}`, value: dollars });
                    onBlur?.();
                  }}
                >
                  <CurrencyAmount
                    value={amount}
                    currency={currency}
                    style="decimal"
                    maximumFractionDigits={fractionDigits}
                    minimumFractionDigits={fractionDigits}
                    isZeroExponentCurrency={isZeroExponentCurrency(currency)}
                  />
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </Field>
  );
};

export default SuggestionsSelection;
